import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../CSS/UpiPayment.css';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

const UpiPayment = () => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [upiId, setUpiId] = useState('ramswaroop.exe@oksbi');
  const [qrValue, setQrValue] = useState('');
  const location = useLocation();
  const [manualAmount, setManualAmount] = useState('');
  const [manualDesc, setManualDesc] = useState('');
  const [isManualInput, setIsManualInput] = useState(false);
  const [isFromUrl, setIsFromUrl] = useState(false);
  
  // Define UPI options
  const upiOptions = [
    { id: 1, value: 'pychunk@upi', label: 'pychunk@upi' },
    { id: 2, value: 'ramswaroop.exe@oksbi', label: 'ramswaroop.exe@oksbi' },
    { id: 3, value: 'ramswaroop@dbs', label: 'ramswaroop@dbs' },
    { id: 4, value: 'rpx@pingpay', label: 'rpx@pingpay' }
  ];

  // Payment app icon URLs from web
  const paymentIcons = {
    phonepe: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVf6nm-Dk1ELbj1aBfeN_vJH6R1wAqYWnltw&s',
    googlepay: 'https://cdn.iconscout.com/icon/free/png-256/free-google-pay-2038779-1721670.png',
    paytm: 'https://cdn.iconscout.com/icon/free/png-256/free-paytm-226448.png',
    amazonpay: '/amazon-pay.svg',
    bhim: 'https://cdn.iconscout.com/icon/free/png-256/free-bhim-3-69845.png',
    qrPlaceholder: 'https://cdn-icons-png.flaticon.com/512/2432/2432572.png'
  };

  useEffect(() => {
    // Hide header and footer
    document.body.classList.add('payment-page');

    // Parse URL parameters for amount, description and UPI ID
    const params = new URLSearchParams(location.search);
    const amountParam = params.get('am');
    const descParam = params.get('desc');
    const upiParam = params.get('upi');

    if (amountParam) {
      setAmount(amountParam);
      setManualAmount(amountParam);
      setIsFromUrl(true); // Mark that data came from URL
    }
    
    if (descParam) {
      setDescription(descParam);
      setManualDesc(descParam);
    }
    
    // Handle UPI ID selection from URL parameter
    if (upiParam) {
      if (upiParam === '1') {
        setUpiId('pychunk@upi');
      } else if (upiParam === '2') {
        setUpiId('ramswaroop.exe@oksbi');
      }
    }

    setIsManualInput(!amountParam);

    // Cleanup function to restore header and footer visibility
    return () => {
      document.body.classList.remove('payment-page');
    };
  }, [location]);

  useEffect(() => {
    // Generate UPI URI when amount, description, or upiId changes
    if (amount) {
      const upiUri = generateUpiUri();
      setQrValue(upiUri);
    }
  }, [amount, description, upiId]);

  const generateUpiUri = () => {
    const encodedDesc = encodeURIComponent(description || 'Payment to RAMSWAROOP PATEL');
    return `upi://pay?pa=${upiId}&pn=RAMSWAROOP%20PATEL&am=${amount}&cu=INR&tn=${encodedDesc}`;
  };

  const handleUpiAppClick = (app) => {
    const upiUri = generateUpiUri();
    
    // Different handling based on app
    switch (app) {
      case 'phonepe':
        window.location.href = `phonepe://${upiUri}`;
        break;
      case 'googlepay':
        window.location.href = `tez://upi/pay?pa=${upiId}&pn=RAMSWAROOP%20PATEL&am=${amount}&cu=INR&tn=${encodeURIComponent(description || 'Payment to RAMSWAROOP PATEL')}`;
        break;
      case 'paytm':
        window.location.href = `paytmmp://${upiUri}`;
        break;
      case 'amazonpay':
        window.location.href = `amazonpay://${upiUri}`;
        break;
      case 'bhim':
        window.location.href = `bhim://${upiUri}`;
        break;
      default:
        window.location.href = upiUri;
    }
  };

  // Handle direct UPI link
  const handleDirectUpiClick = () => {
    const upiUri = generateUpiUri();
    window.location.href = upiUri;
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    
    if (manualAmount && !isNaN(manualAmount)) {
      setAmount(manualAmount);
      setDescription(manualDesc || 'Payment to RAMSWAROOP PATEL');
      setIsManualInput(false);
    }
  };

  const handleUpiChange = (e) => {
    setUpiId(e.target.value);
  };

  return (
    <div className="upi-payment-fullpage">
      <div className="upi-payment-container">
        <div className="payment-card">
          <div className="payment-header">
            <div className="payment-logo">
              <span className="logo-text">R</span>
              <span className="logo-subtitle">PAY</span>
            </div>
            <h1>{t('UPI Payment')}</h1>
          </div>

          <div className="payment-details">
            {isManualInput ? (
              <form onSubmit={handleManualSubmit} className="manual-input-form">
                <div className="input-group">
                  <label htmlFor="amount">Amount (₹)</label>
                  <input
                    type="number"
                    id="amount"
                    min="1"
                    step="1"
                    value={manualAmount}
                    onChange={(e) => setManualAmount(e.target.value)}
                    required
                    placeholder="Enter amount"
                  />
                </div>
                
                <div className="input-group">
                  <label htmlFor="description">Description (Optional)</label>
                  <input
                    type="text"
                    id="description"
                    value={manualDesc}
                    onChange={(e) => setManualDesc(e.target.value)}
                    placeholder="Payment description"
                  />
                </div>
                
                <div className="input-group">
                  <label htmlFor="upi-select">UPI ID</label>
                  <select 
                    id="upi-select" 
                    value={upiId} 
                    onChange={handleUpiChange}
                    className="upi-select"
                  >
                    {upiOptions.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <button type="submit" className="proceed-button">
                  Generate Payment
                </button>
              </form>
            ) : (
              <>
                <div className="amount-display">
                  <span className="rupee-symbol">₹</span>
                  <span className="amount-value">{amount}</span>
                  {!isFromUrl && (
                    <button 
                      className="edit-button"
                      onClick={() => setIsManualInput(true)}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  )}
                </div>
                <p className="payment-description">{description || 'Payment to RAMSWAROOP PATEL'}</p>
                
                <div className="upi-selection">
                  <label>UPI ID:</label>
                  <select 
                    value={upiId} 
                    onChange={handleUpiChange}
                    className="upi-select-inline"
                  >
                    {upiOptions.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

          {!isManualInput && amount && (
            <>
              <div className="payment-methods">
                <div className="qr-container">
                  <div className="qr-code-wrapper">
                    <QRCodeCanvas 
                      value={qrValue} 
                      size={250}
                      level={"H"}
                      includeMargin={true}
                      renderAs={"canvas"}
                    />
                    <div className="qr-logo"></div>
                  </div>
                  <p className="scan-text">Scan to pay with any UPI app</p>
                  
                  {/* Direct UPI button */}
                  <button onClick={handleDirectUpiClick} className="direct-upi-button">
                    <i className="fas fa-external-link-alt"></i> Pay with UPI Apps
                  </button>
                </div>

                <div className="or-divider">
                  <span>OR</span>
                </div>

                <div className="upi-apps">
                  <h2>{t('Pay using')}</h2>
                  <div className="app-buttons">
                    <button onClick={() => handleUpiAppClick('phonepe')} className="app-button">
                      <img src={paymentIcons.phonepe} alt="PhonePe" />
                      <span>PhonePe</span>
                    </button>
                    <button onClick={() => handleUpiAppClick('googlepay')} className="app-button">
                      <img src={paymentIcons.googlepay} alt="Google Pay" />
                      <span>Google Pay</span>
                    </button>
                    <button onClick={() => handleUpiAppClick('paytm')} className="app-button">
                      <img src={paymentIcons.paytm} alt="Paytm" />
                      <span>Paytm</span>
                    </button>
                    <button onClick={() => handleUpiAppClick('amazonpay')} className="app-button">
                      <img src={paymentIcons.amazonpay} alt="Amazon Pay" />
                      <span>Amazon Pay</span>
                    </button>
                    <button onClick={() => handleUpiAppClick('bhim')} className="app-button">
                      <img src={paymentIcons.bhim} alt="BHIM UPI" />
                      <span>BHIM</span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="payment-footer">
                <div className="secure-badge">
                  <i className="fas fa-shield-alt"></i> Secure Payment
                </div>
                <p>UPI ID: <strong>{upiId}</strong></p>
                <p className="payment-note">{t('Scan the QR code or click on any UPI app button to make payment')}</p>
              </div>
            </>
          )}

          {(!amount && !isManualInput) && (
            <div className="no-amount-message">
              <div className="qr-placeholder">
                <img src={paymentIcons.qrPlaceholder} alt="QR Placeholder" />
                <p>Please specify an amount in the URL</p>
                <p>Example: /pay?am=100&desc=Donation&upi=1</p>
                <button 
                  className="manual-input-button"
                  onClick={() => setIsManualInput(true)}
                >
                  Enter Amount Manually
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpiPayment;